// Clerk loader for id360admin.
//
// We do NOT `import '@clerk/clerk-js'` — this app's Babel (react-scripts 3.4
// fork, webpack 4) cannot parse Clerk's modern source. Instead, Clerk's
// prebuilt dist/ is copied to /clerk/ at build time by config-overrides.js,
// and we load clerk.browser.js here via a runtime <script> tag.
//
// clerk.browser.js is an auto-initializing bundle: on load it reads the
// publishable key from the script tag's data-clerk-publishable-key attribute
// and sets window.Clerk to a live *instance* (not a constructor). Without the
// data attribute it throws "Missing publishableKey".
//
// See packages/apps/id360/docs/CLERK_INTEGRATION.md for the full rationale.

const CLERK_SCRIPT_ID = 'id360-clerk-js-sdk';
const DEFAULT_CLERK_JS_URL = '/clerk/clerk.browser.js';

let clerkInstancePromise;
let clerkScriptPromise;

export const getClerkPublishableKey = () => process.env.REACT_APP_CLERK_PUBLISHABLE_KEY || '';
export const getClerkJsUrl = () => process.env.REACT_APP_CLERK_JS_URL || DEFAULT_CLERK_JS_URL;
export const isClerkConfigured = () => Boolean(getClerkPublishableKey());

const loadClerkScript = () => {
  if (typeof window === 'undefined' || !isClerkConfigured()) {
    return Promise.resolve(null);
  }

  if (window.Clerk) {
    return Promise.resolve(window.Clerk);
  }

  if (!clerkScriptPromise) {
    clerkScriptPromise = new Promise((resolve, reject) => {
      const publishableKey = getClerkPublishableKey();
      const existing = document.getElementById(CLERK_SCRIPT_ID);

      const finish = () => {
        if (window.Clerk) {
          resolve(window.Clerk);
          return;
        }
        reject(new Error('Clerk SDK loaded without a global Clerk instance.'));
      };

      if (existing) {
        existing.addEventListener('load', finish, { once: true });
        existing.addEventListener('error', reject, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.id = CLERK_SCRIPT_ID;
      script.async = true;
      script.setAttribute('crossorigin', 'anonymous');
      // Required: clerk.browser.js reads the key from this attribute on its
      // own <script> tag at load time. Omitting it throws Missing publishableKey.
      script.setAttribute('data-clerk-publishable-key', publishableKey);
      script.src = getClerkJsUrl();
      script.onload = finish;
      script.onerror = reject;
      document.head.appendChild(script);
    }).catch((error) => {
      clerkScriptPromise = null;
      throw error;
    });
  }

  return clerkScriptPromise;
};

export const getClerkInstance = async () => {
  if (typeof window === 'undefined' || !isClerkConfigured()) {
    return null;
  }

  if (!clerkInstancePromise) {
    clerkInstancePromise = (async () => {
      const clerk = await loadClerkScript();
      if (!clerk) {
        return null;
      }

      // Auto-init gives us the instance, but .load() still has to run once
      // before session/user APIs are safe. Guard with `.loaded` so repeat
      // callers don't re-trigger Clerk's network handshake.
      if (!clerk.loaded) {
        await clerk.load();
      }
      return clerk;
    })().catch((error) => {
      clerkInstancePromise = null;
      throw error;
    });
  }

  return clerkInstancePromise;
};

export const getClerkToken = async () => {
  try {
    const clerk = await getClerkInstance();
    if (!clerk || !clerk.session) {
      return null;
    }

    return await clerk.session.getToken();
  } catch (error) {
    return null;
  }
};

export const clerkSignOut = async () => {
  try {
    const clerk = await getClerkInstance();
    if (!clerk) {
      return;
    }

    await clerk.signOut();
  } catch (error) {
    // Swallow sign-out failures so logout can still clear local state.
  }
};
